import { apiClient } from './client'

// ── Salary ───────────────────────────────────────────────────────────────────
export interface SalaryStructure {
  id: string
  org_id: string
  user_id: string
  currency: string
  basic: number
  per_hour_rate: number
  house_rent: number
  medical: number
  conveyance: number
  other_allowances: number
  allowances_total: number
  gross: number
  effective_from: string
  is_active: boolean
  notes?: string | null
  created_at: string
  updated_at: string
}

export interface SalaryInput {
  currency?: string
  basic: number
  per_hour_rate: number
  house_rent: number
  medical: number
  conveyance: number
  other_allowances: number
  effective_from: string
  notes?: string | null
}

export async function getMySalary(): Promise<SalaryStructure | null> {
  const res = await apiClient.get<SalaryStructure | null>('/salary/me')
  return res.data
}
export async function getUserSalary(userId: string): Promise<SalaryStructure | null> {
  const res = await apiClient.get<SalaryStructure | null>(`/salary/user/${userId}`)
  return res.data
}
export async function setUserSalary(userId: string, data: SalaryInput): Promise<SalaryStructure> {
  const res = await apiClient.put<SalaryStructure>(`/salary/user/${userId}`, data)
  return res.data
}

// ── Payroll runs & payslips ──────────────────────────────────────────────────
export interface Payslip {
  id: string
  run_id: string
  user_id: string
  user_name?: string | null
  employee_id?: string | null
  designation?: string | null
  date_of_joining?: string | null
  email?: string | null
  cnic?: string | null
  bank_code?: string | null
  bank_account_number?: string | null
  run_month?: number | null
  run_year?: number | null
  currency: string
  working_days: number
  present_days: number
  paid_leave_days: number
  lop_days: number
  basic: number
  per_hour_rate: number
  overtime_hours: number
  overtime_earning: number
  medical_reimbursement: number
  commission: number
  adjustment: number
  gross: number
  provident_fund: number
  loan_deducted: number
  loan_deduction_month: number
  loan_applied: number
  remaining_loan: number
  food: number
  gym_membership: number
  wht: number
  total_deduction: number
  net: number
  status: string
  notes?: string | null
  created_at: string
  updated_at: string
}

// Locked self-serve list — period + status only (amounts unlocked via viewMyPayslip)
export interface MyPayslipSummary {
  id: string
  run_month?: number | null
  run_year?: number | null
  currency: string
  status: string
}

// Editable per-month fields the accountant fills in (draft only).
export const PAYSLIP_EDIT_FIELDS = [
  'basic', 'per_hour_rate', 'overtime_hours', 'medical_reimbursement', 'commission',
  'adjustment', 'provident_fund', 'loan_deducted', 'loan_deduction_month',
  'loan_applied', 'remaining_loan', 'food', 'gym_membership', 'wht',
] as const
export type PayslipEditField = (typeof PAYSLIP_EDIT_FIELDS)[number]

export interface PayrollRun {
  id: string
  org_id: string
  month: number
  year: number
  status: string
  notes?: string | null
  created_by?: string | null
  finalized_at?: string | null
  payslip_count: number
  total_net: number
  created_at: string
  updated_at: string
}

export interface PayrollRunDetail extends PayrollRun {
  payslips: Payslip[]
}

export type PayslipUpdate = Partial<Record<PayslipEditField, number>> & { notes?: string | null }

export async function listRuns(): Promise<PayrollRun[]> {
  const res = await apiClient.get<PayrollRun[]>('/payroll/runs')
  return res.data
}
export async function createRun(data: { month: number; year: number; notes?: string }): Promise<PayrollRunDetail> {
  const res = await apiClient.post<PayrollRunDetail>('/payroll/runs', data)
  return res.data
}
export async function getRun(id: string): Promise<PayrollRunDetail> {
  const res = await apiClient.get<PayrollRunDetail>(`/payroll/runs/${id}`)
  return res.data
}
export async function regenerateRun(id: string): Promise<PayrollRunDetail> {
  const res = await apiClient.post<PayrollRunDetail>(`/payroll/runs/${id}/regenerate`)
  return res.data
}
export async function finalizeRun(id: string): Promise<PayrollRunDetail> {
  const res = await apiClient.post<PayrollRunDetail>(`/payroll/runs/${id}/finalize`)
  return res.data
}
export async function deleteRun(id: string): Promise<void> {
  await apiClient.delete(`/payroll/runs/${id}`)
}
export async function updatePayslip(id: string, data: PayslipUpdate): Promise<Payslip> {
  const res = await apiClient.put<Payslip>(`/payroll/payslips/${id}`, data)
  return res.data
}
export async function emailPayslip(id: string): Promise<void> {
  await apiClient.post(`/payroll/payslips/${id}/email`)
}
export async function myPayslips(): Promise<MyPayslipSummary[]> {
  const res = await apiClient.get<MyPayslipSummary[]>('/payroll/payslips/mine')
  return res.data
}
export async function viewMyPayslip(id: string, code: string): Promise<Payslip> {
  const res = await apiClient.post<Payslip>(`/payroll/payslips/${id}/view`, { code })
  return res.data
}

function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

export async function downloadPayslipPdf(id: string, label = 'payslip'): Promise<void> {
  const res = await apiClient.get(`/payroll/payslips/${id}/pdf`, { responseType: 'blob' })
  saveBlob(res.data as Blob, `${label}.pdf`)
}

export async function exportRun(id: string, format: 'csv' | 'xlsx' = 'xlsx', label = 'payroll'): Promise<void> {
  const res = await apiClient.get(`/payroll/runs/${id}/export`, {
    params: { format },
    responseType: 'blob',
  })
  saveBlob(res.data as Blob, `${label}.${format}`)
}

export interface PayrollImportIssue { row: number; employee_id?: string | null; message: string }
export interface PayrollImportRow {
  row: number
  payslip_id: string
  employee_id?: string | null
  user_name?: string | null
  changes: { field: string; label: string; old: number | string; new: number | string }[]
  net_before: number | string
  net_after: number | string
}
export interface PayrollImportResult {
  committed: boolean
  updated: number
  unchanged: number
  rows: PayrollImportRow[]
  skipped: PayrollImportIssue[]
  errors: PayrollImportIssue[]
  run?: PayrollRunDetail | null
}

// Upload an edited salary sheet. commit=false previews the changes, true applies them.
export async function importRun(id: string, file: File, commit: boolean): Promise<PayrollImportResult> {
  const form = new FormData()
  form.append('file', file)
  const res = await apiClient.post<PayrollImportResult>(`/payroll/runs/${id}/import`, form, {
    params: { commit },
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return res.data
}

export const MONTHS = ['', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

export function money(v: number | string | null | undefined, currency = 'PKR'): string {
  const n = Number(v ?? 0)
  return `${currency} ${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
}
